import { createFormulaReadContext } from './formula-read-context';
import { createCatalogDefaultProviderReader } from './provider-catalog-order';
import { readNodeItemId } from './layer-cell-readers';
import { readItemField } from './item-field-reader';
import { constructInventoryDefinitionShells } from './from-base-inventory-references';
import type { InventoryReferenceMetadata } from './from-base-inventory-references';
import { constructInventoryDefinitionChoices } from './from-base-inventory-choices';
import { normalizeInventoryDefinition } from './from-base-inventory-normalization';
import { constructInventoryDefinitionAudio, constructInventoryDefinitionVideos } from './from-base-inventory-media';
import type { InventoryMediaMetadata, InventoryVideoContext } from './from-base-inventory-media';
import { assertUncomputedFromBase, fromBaseClone, fromBaseRecord, fromBaseUuid, FromBaseInputMissing, FromBaseStageMissing } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';
import type { FromBaseEncounterShellMetadata } from './from-base-encounter-shell';
import { phpJsonInt, phpJsonTruthy } from './php-json-values';
import { constructPlayerDefinitionPictures } from './from-base-player-media';
import { surfaceField } from './from-base-encounter-media';

/** Source-table presentation shared by the encounter's weather/status windows.
 * Authored layers remain separate from the completed definition producer. */
export function compileEncounterDefinitionSurface(source: Row, cdnUrl?: string): Row {
    const items: Row = {};
    for (const name of ['item_created_by_id', 'item_id', 'item_name', 'item_type', 'item_description', 'flavor_text',
        'source_link', 'preview', 'homebrew', 'item_private', 'item_deleted', 'item_archived', 'item_price']) {
        const value = readItemField(source, name);
        const blank = ['item_description', 'flavor_text', 'source_link'].includes(name) ? value == null || value === ''
            : ['preview', 'homebrew', 'item_private', 'item_deleted', 'item_archived'].includes(name) ? !phpJsonTruthy(value)
            : name === 'item_price' ? Number(value ?? 0) === 0 : false;
        if (!blank) items[name] = value;
    }
    const sourceId = readItemField(source, 'source_id');
    if (typeof sourceId === 'string' && sourceId !== '' && sourceId !== 'D887B8A7-2CED-AB81-3E4E-DDDF4ACD91AE')
        items.source = Object.fromEntries(['source_id', 'source_name', 'source_abbreviation', 'source_license_kind']
            .map(name => [name, readItemField(source, name) ?? '']));
    const result: Row = { items };
    const pictures = constructPlayerDefinitionPictures(source, cdnUrl, surfaceField);
    if (pictures.length) result.pictures = { item: pictures };
    return result;
}

function createEncounterStatusRawDefinitionReader(catalog: Readonly<Record<string, Row>>, missingIds: readonly string[]) {
    assertUncomputedFromBase(catalog, 'status-catalog');
    return (id: string): Row | null => {
        if (!fromBaseUuid(id)) throw new TypeError(`Invalid status definition reference: ${id}`);
        const source = catalog[id] ?? catalog[id.toUpperCase()];
        if (!source) {
            if (missingIds.some(miss => miss.toUpperCase() === id.toUpperCase())) return null;
            throw new FromBaseInputMissing('status-definition', id);
        }
        const identity = readItemField(source, 'item_id');
        if (!fromBaseUuid(identity) || identity.toUpperCase() !== id.toUpperCase()) throw new TypeError(`Invalid status definition identity: ${id}`);
        if (phpJsonInt(readItemField(source, 'item_deleted')) !== 0) throw new TypeError(`Deleted status definition: ${id}`);
        return source;
    };
}

/** Definition LAYERS only. Media, PL, display normalization and full nests belong
 * to the complete definition constructor; this projection never impersonates it. */
export function createEncounterStatusDefinitionProjection(catalog: Readonly<Record<string, Row>>,
    missingIds: readonly string[], metadata: FromBaseEncounterShellMetadata,
    references: InventoryReferenceMetadata, objectTypes: readonly string[]) {
    const raw = createEncounterStatusRawDefinitionReader(catalog, missingIds);
    const gaps = (detail: string): never => { throw new FromBaseStageMissing('encounter-status-definition-finalization', detail); };
    const dependencies = { catalogRows: metadata.layerCatalog, abilityTypes: metadata.abilityTypes,
        aggregationStrategy: (name: string) => metadata.layerCatalog[name]?.aggregation_strategy ?? 'host_wins',
        resolveCarrierRef: raw, resolveInventoryInstance: (id: string) => gaps('inventory:' + id) };
    const readers = createFormulaReadContext(dependencies);
    const providers = createCatalogDefaultProviderReader({ catalogRows: metadata.layerCatalog, nodeItemId: readNodeItemId, resolveCarrierRef: raw });
    const checked = new Set<string>();
    const check = (source: Row): void => {
        const id = readNodeItemId(source);
        if (id && checked.has(id)) return;
        if (id) checked.add(id);
        const type = readItemField(source, 'item_type');
        if (objectTypes.includes(String(type)) || ![...metadata.abilityTypes, 'spell', 'status_effect', 'damage_type', 'terrain_type', 'size_type'].includes(String(type)))
            gaps('classification:' + id);
        for (const name of ['template_id', 'enchantment_template_id', 'enchantment_template_applied'])
            if (phpJsonTruthy(readers.layerValue(source, name))) gaps(name + ':' + id);
        if (!fromBaseRecord(source.item_layer_types) && !Array.isArray(source.item_layer_types)) throw new TypeError('Invalid status definition layers');
        for (const name of Object.keys(source.item_layer_types))
            if (references.layerCatalog[name]?.full_nest && name !== 'size'
                && Object.keys(readers.layerEntries(source, name)).length) gaps('full-nest:' + id + ':' + name);
        for (const donor of providers.providers(source)) check(donor as Row);
    };
    return (id: string): Row | null => {
        const source = raw(id);
        if (!source) return null;
        check(source);
        // Authored layers feed this checkpoint. Display reference expansion stays
        // with the complete definition constructor, never a warm-cache shortcut.
        let result = fromBaseClone(source);
        for (const name of Object.keys(result.item_layer_types as Row)) delete result[name];
        return constructInventoryDefinitionChoices(result, readers.layerEntries, raw);
    };
}

/** Native reference-emission window over held raw status definitions. Full nests,
 * classification, display metadata, media and PL still require finalization. */
export function createEncounterStatusDefinitionShellProjection(catalog: Readonly<Record<string, Row>>,
    missingIds: readonly string[], metadata: FromBaseEncounterShellMetadata,
    references: InventoryReferenceMetadata, objectTypes: readonly string[]) {
    const definition = createEncounterStatusDefinitionProjection(catalog, missingIds, metadata, references, objectTypes);
    // A shell needs only the referenced identity. It is not itself a status definition.
    const raw = createEncounterStatusRawDefinitionReader(catalog, missingIds);
    return (id: string): Row | null => {
        const source = definition(id);
        if (!source) return null;
        const result = constructInventoryDefinitionShells(source, references, raw);
        // Native mirror_layers_into_bucket elides the empty outer layer bucket.
        if (!Object.keys(result.item_layer_types as Row).length) delete result.item_layer_types;
        return result;
    };
}

/** Post-reference presentation only; never marks a definition fully constructed.
 * SELECT closure for videos includes explicit empty galleries. */
export function createEncounterStatusPresentationProjection(catalog: Readonly<Record<string, Row>>,
    missingIds: readonly string[], metadata: FromBaseEncounterShellMetadata,
    references: InventoryReferenceMetadata, objectTypes: readonly string[],
    media: InventoryMediaMetadata, videos: InventoryVideoContext) {
    const shell = createEncounterStatusDefinitionShellProjection(catalog, missingIds, metadata, references, objectTypes);
    const raw = createEncounterStatusRawDefinitionReader(catalog, missingIds);
    const readers = createFormulaReadContext({ catalogRows: metadata.layerCatalog, abilityTypes: metadata.abilityTypes,
        aggregationStrategy: (name: string) => metadata.layerCatalog[name]?.aggregation_strategy ?? 'host_wins',
        resolveCarrierRef: raw, resolveInventoryInstance: id => {
            throw new FromBaseStageMissing('encounter-status-definition-finalization', 'inventory:' + id);
        } });
    return (id: string): Row | null => {
        const source = shell(id);
        if (!source) return null;
        const normalized = normalizeInventoryDefinition(source, metadata.layerCatalog);
        const audio = constructInventoryDefinitionAudio(normalized, media, readers.layerValue,
            (row, name) => readers.layerValue(row, name) !== null || Object.keys(readers.layerEntries(row, name)).length > 0);
        return constructInventoryDefinitionVideos(audio, videos);
    };
}
